import { useState, useRef, useCallback } from 'react';
import { useLocation, useSearch, Link } from 'wouter';
import { useCreateArtefact, useGetProjects, useGetProjectActivities, useDetectTranscriptSplits } from '@edison/api-client-react';
import { AlertCircle, FileText, Upload, X, ChevronDown, Sparkles, CheckCircle, Plus, Users } from 'lucide-react';
import mammoth from 'mammoth';
import { Button } from '@/components/ui/button.js';
import { formatFy } from '@/lib/utils.js';

const ARTEFACT_TYPES = [
  { value: 'meeting_transcript', label: 'Meeting Transcript' },
  { value: 'prd_document',       label: 'PRD Document' },
  { value: 'llm_session',        label: 'LLM Session' },
  { value: 'manual_note',        label: 'Manual Note' },
  { value: 'market_search',      label: 'Market Search' },
] as const;

type ArtefactTypeValue = (typeof ARTEFACT_TYPES)[number]['value'];

function detectType(filename: string, content: string): ArtefactTypeValue {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'vtt') return 'meeting_transcript';
  const lines = content.split('\n').slice(0, 30);
  const hasTimestampLine = lines.some((l) => /\b\d{1,2}:\d{2}\b/.test(l));
  const hasSpeakerLine   = lines.some((l) => /^[A-ZÀ-Ö][a-zà-ö]+(?: [A-ZÀ-Ö][a-zà-ö]+)?\s+\d{1,2}:\d{2}/.test(l));
  const hasColon         = lines.some((l) => /^[A-Z][^:]{1,40}:\s/.test(l));
  if (hasTimestampLine || hasSpeakerLine || hasColon) return 'meeting_transcript';
  if ((ext === 'md' || ext === 'txt') && /\bprd\b|product.{0,10}req|spec|design.{0,10}doc/i.test(filename)) {
    return 'prd_document';
  }
  return 'manual_note';
}

function cleanTitle(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Mode = 'dropzone' | 'splitting' | 'split-detected' | 'file-loaded' | 'manual';

interface Attendee {
  name:     string;
  email:    string | null;
  userId:   string | null;
  userName: string | null;
}

interface DetectedSplit {
  projectId:             string;
  projectName:           string;
  activityIds:           string[];
  activityNames:         string[];
  suggestedActivityName: string | null;
  title:                 string;
  excerpt:               string;
  timeStart:             string | null;
  timeEnd:               string | null;
  attendees:             Attendee[] | null;
}

interface FileProcessingState {
  name:        string;
  size:        number;
  status:      'analysing' | 'done' | 'error';
  splitCount:  number;
  errorReason: 'timeout' | 'ai_error' | 'read_error' | null;
}

interface SplitCardProps {
  split:    DetectedSplit;
  index:    number;
  projects: Array<{ id: string; name: string; incomeYear?: string | null }>;
  onChange: (index: number, patch: Partial<DetectedSplit>) => void;
  onRemove: (index: number) => void;
}

function SplitCard({ split, index, projects, onChange, onRemove }: SplitCardProps) {
  const [showExcerpt, setShowExcerpt] = useState(false);
  const { data: activitiesData } = useGetProjectActivities(split.projectId);
  const loaded = (activitiesData?.data?.items ?? []) as Array<{ id: string; name: string; projectId: string }>;

  // Include AI-matched activities even if the list hasn't populated yet
  const extraActivities = split.activityIds
    .filter((id, i) => split.activityNames[i] && !loaded.find((a) => a.id === id))
    .map((id, i) => ({ id, name: split.activityNames[i]!, projectId: split.projectId }));
  const projectActivities = extraActivities.length > 0 ? [...extraActivities, ...loaded] : loaded;

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
          <select
            value={split.projectId}
            onChange={(e) => onChange(index, {
              projectId:    e.target.value,
              projectName:  projects.find((p) => p.id === e.target.value)?.name ?? split.projectName,
              activityIds:  [],
              activityNames: [],
            })}
            className="text-sm font-medium bg-transparent border-0 p-0 focus:ring-0 cursor-pointer max-w-[200px]"
            aria-label="Project"
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.incomeYear ? ` (${formatFy(p.incomeYear)})` : ''}
              </option>
            ))}
          </select>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">Meeting Transcript</span>
          {(split.timeStart ?? split.timeEnd) && (
            <>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground font-mono">
                {split.timeStart ?? '—'} – {split.timeEnd ?? '—'}
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="rounded-md p-1 hover:bg-accent shrink-0"
          aria-label="Remove this split"
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </div>

      {/* Title */}
      <input
        type="text"
        value={split.title}
        onChange={(e) => onChange(index, { title: e.target.value })}
        className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm"
        placeholder="Title"
        aria-label="Title"
      />

      {/* Activity tags — multi-select */}
      {projectActivities.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Activities <span className="font-normal">(select all that apply)</span></p>
          <div className="flex flex-wrap gap-1.5">
            {projectActivities.map((a) => {
              const isSelected = split.activityIds.includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    const newIds = isSelected
                      ? split.activityIds.filter((id) => id !== a.id)
                      : [...split.activityIds, a.id];
                    const newNames = isSelected
                      ? split.activityNames.filter((_, i) => split.activityIds[i] !== a.id)
                      : [...split.activityNames, a.name];
                    onChange(index, { activityIds: newIds, activityNames: newNames, suggestedActivityName: null });
                  }}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'border bg-background text-muted-foreground hover:text-foreground hover:border-primary/50'
                  }`}
                >
                  {isSelected && <Sparkles className="h-3 w-3" aria-hidden="true" />}
                  {a.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* New activity suggestion */}
      {split.activityIds.length === 0 && split.suggestedActivityName && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 px-3 py-2">
          <Plus className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
          <span className="text-xs text-primary flex-1">
            New activity suggested: <strong>{split.suggestedActivityName}</strong>
          </span>
          <button
            type="button"
            onClick={() => onChange(index, { suggestedActivityName: null })}
            className="text-xs text-muted-foreground hover:text-foreground"
            aria-label="Dismiss suggestion"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Attendees */}
      {split.attendees && split.attendees.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
            <Users className="h-3 w-3" aria-hidden="true" /> Attendees
          </p>
          <div className="flex flex-wrap gap-1.5">
            {split.attendees.map((a, ai) => {
              const initials = (a.userName ?? a.name)
                .split(' ')
                .map((w) => w[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
              return (
                <span
                  key={ai}
                  title={a.email ?? a.name}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    a.userId
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'border bg-background text-muted-foreground'
                  }`}
                >
                  <span className="font-mono text-[10px] opacity-70">{initials}</span>
                  {a.userName ?? a.name}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Excerpt preview */}
      <button
        type="button"
        onClick={() => setShowExcerpt((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        aria-expanded={showExcerpt}
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showExcerpt ? 'rotate-180' : ''}`} aria-hidden="true" />
        {showExcerpt ? 'Hide' : 'Preview'} excerpt ({split.excerpt.split('\n').length} lines)
      </button>
      {showExcerpt && (
        <pre className="rounded-lg border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
          {split.excerpt}
        </pre>
      )}
    </div>
  );
}

export function ArtefactCreatePage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const presetProjectId = params.get('projectId') ?? '';

  const [mode, setMode]               = useState<Mode>('dropzone');
  const [isDragging, setIsDragging]   = useState(false);
  const [fileName, setFileName]       = useState('');
  const [fileSize, setFileSize]       = useState(0);
  const [type, setType]               = useState<ArtefactTypeValue>('manual_note');
  const [title, setTitle]             = useState('');
  const [content, setContent]         = useState('');
  const [showContent, setShowContent] = useState(false);
  const [selectedProjectId, setSelectedProjectId]   = useState(presetProjectId);
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [titleError, setTitleError]   = useState('');
  const [contentError, setContentError] = useState('');
  const [splits, setSplits]           = useState<DetectedSplit[]>([]);
  const [processingFiles, setProcessingFiles] = useState<FileProcessingState[]>([]);
  const [savingCount, setSavingCount] = useState(0);
  const [savedCount, setSavedCount]   = useState(0);

  const fileInputRef   = useRef<HTMLInputElement>(null);
  const pendingCountRef = useRef(0);

  const { mutate: createArtefact, isPending, error: mutationError } = useCreateArtefact();
  const { mutate: detectSplits } = useDetectTranscriptSplits();
  const { data: projectsData, isLoading: projectsLoading } = useGetProjects({ status: 'active' });
  const { data: activitiesData } = useGetProjectActivities(selectedProjectId);

  const projects   = projectsData?.data?.items ?? [];
  const activities = (activitiesData?.data?.items ?? []) as Array<{ id: string; name: string; projectId: string }>;

  const loadFiles = useCallback((fileList: File[]) => {
    if (fileList.length === 0) return;

    const isSingle = fileList.length === 1;
    const fileStates: FileProcessingState[] = fileList.map((f) => ({
      name: f.name, size: f.size, status: 'analysing', splitCount: 0, errorReason: null,
    }));
    setProcessingFiles(fileStates);
    setSplits([]);
    setMode('splitting');
    pendingCountRef.current = fileList.length;

    fileList.forEach(async (file, i) => {
      let text: string;
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      try {
        if (ext === 'docx') {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          text = result.value;
        } else {
          text = await file.text();
        }
      } catch {
        if (isSingle) {
          setMode('dropzone');
        } else {
          setProcessingFiles((prev) => prev.map((f, j) => j === i ? { ...f, status: 'error', errorReason: 'read_error' } : f));
          pendingCountRef.current--;
          if (pendingCountRef.current === 0) setMode('split-detected');
        }
        return;
      }

      // Always populate the manual-form state so file-loaded mode works as fallback
      setFileName(file.name);
      setFileSize(file.size);
      const det = ext === 'docx' ? 'meeting_transcript' : detectType(file.name, text);
      setType(det);
      setTitle(cleanTitle(file.name));
      setContent(text);
      setShowContent(false);

      // Cap analysis content at 150k chars. Since split v1.3 the model returns short boundary
      // quotes (not verbatim excerpts), so output is small and large inputs fit the ~29s
      // gateway window. The FULL text is still stored as the artefact content either way.
      const cappedContent = text.length > 150000 ? text.slice(0, 150000) : text;

      detectSplits(
        { data: { content: cappedContent, filename: file.name } },
        {
          onSuccess: (res) => {
            type ApiSplit = Omit<DetectedSplit, 'activityIds' | 'activityNames'> & { activityId?: string | null; activityName?: string | null };
            const data = (res as { data?: { splits?: ApiSplit[] } }).data;
            const rawSplits = data?.splits ?? [];
            if (isSingle && rawSplits.length === 0) {
              setMode('file-loaded');
              return;
            }
            // Normalise legacy single activityId → activityIds array
            const detected: DetectedSplit[] = rawSplits.map((s) => ({
              ...s,
              activityIds:  s.activityId ? [s.activityId] : [],
              activityNames: s.activityName ? [s.activityName] : [],
            }));
            setSplits((prev) => [...prev, ...detected]);
            setProcessingFiles((prev) => prev.map((f, j) => j === i ? { ...f, status: 'done', splitCount: detected.length } : f));
            pendingCountRef.current--;
            if (pendingCountRef.current === 0) setMode('split-detected');
          },
          onError: (err: unknown) => {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const reason: FileProcessingState['errorReason'] = status === 504 || status === 408 ? 'timeout' : 'ai_error';
            if (isSingle) {
              setMode('file-loaded');
              return;
            }
            setProcessingFiles((prev) => prev.map((f, j) => j === i ? { ...f, status: 'error', errorReason: reason } : f));
            pendingCountRef.current--;
            if (pendingCountRef.current === 0) setMode('split-detected');
          },
        }
      );
    });
  }, [detectSplits]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) loadFiles(files);
  }, [loadFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) loadFiles(files);
  };

  const validate = () => {
    let ok = true;
    if (!title.trim()) { setTitleError('Title is required'); ok = false; } else setTitleError('');
    if (!content.trim()) { setContentError('Content is required'); ok = false; } else setContentError('');
    return ok;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    type CreatePayload = Parameters<typeof createArtefact>[0] & { activityId?: string };
    const payload: CreatePayload = {
      type,
      title: title.trim(),
      content: content.trim(),
      ...(selectedProjectId  ? { projectId:  selectedProjectId  } : {}),
      ...(selectedActivityId ? { activityId: selectedActivityId } : {}),
    };
    createArtefact(payload as Parameters<typeof createArtefact>[0], {
      onSuccess: () => navigate(presetProjectId ? `/projects/${presetProjectId}` : '/artefacts'),
    });
  };

  const handleSaveSplits = () => {
    const toSave = splits.filter((s) => s.title.trim() && s.excerpt.trim());
    setSavingCount(toSave.length);
    setSavedCount(0);
    let done = 0;

    for (const split of toSave) {
      type CreatePayload = Parameters<typeof createArtefact>[0] & { activityIds?: string[] };
      const payload: CreatePayload = {
        type:    'meeting_transcript',
        title:   split.title.trim(),
        content: split.excerpt.trim(),
        projectId: split.projectId,
        ...(split.activityIds.length > 0 ? { activityIds: split.activityIds } : {}),
      };
      createArtefact(payload as Parameters<typeof createArtefact>[0], {
        onSuccess: () => {
          done++;
          setSavedCount(done);
          if (done === toSave.length) navigate('/artefacts');
        },
      });
    }
  };

  const updateSplit = (index: number, patch: Partial<DetectedSplit>) => {
    setSplits((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const removeSplit = (index: number) => {
    setSplits((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelHref = presetProjectId ? `/projects/${presetProjectId}` : '/artefacts';
  const typeLabel  = ARTEFACT_TYPES.find((t) => t.value === type)?.label ?? type;

  // ── Dropzone ──────────────────────────────────────────────────────────────
  if (mode === 'dropzone') {
    return (
      <div className="max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Add evidence</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drop a file and Edison reads it — detecting the project, activity, and time range automatically.
          </p>
        </div>

        <div
          role="button"
          tabIndex={0}
          aria-label="Drop files to upload"
          onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragOver={(e)  => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          className={`
            flex flex-col items-center justify-center gap-3
            rounded-xl border-2 border-dashed
            px-8 py-16 text-center cursor-pointer
            transition-colors select-none
            ${isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30'}
          `}
        >
          <div className={`rounded-full p-4 transition-colors ${isDragging ? 'bg-primary/10' : 'bg-muted'}`}>
            <Upload className={`h-7 w-7 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} aria-hidden="true" />
          </div>
          <div>
            <p className="font-medium text-sm">
              {isDragging ? 'Drop to upload' : 'Drop files here, or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              .txt · .vtt · .md · .docx — drop multiple files to process in parallel
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.vtt,.md,.docx"
          multiple
          onChange={handleFileInput}
          className="hidden"
          aria-hidden="true"
        />

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => { setMode('manual'); setType('manual_note'); }}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Write manually instead
          </button>
        </div>
      </div>
    );
  }

  // ── Splitting ─────────────────────────────────────────────────────────────
  if (mode === 'splitting') {
    const analysingCount = processingFiles.filter((f) => f.status === 'analysing').length;
    return (
      <div className="max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            {processingFiles.length === 1 ? 'Reading your file…' : `Reading ${processingFiles.length} files…`}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {analysingCount > 0
              ? `Identifying projects, activities, and time ranges${processingFiles.length > 1 ? ' in parallel' : ''}.`
              : 'Almost done…'}
          </p>
        </div>

        <div className="space-y-2">
          {processingFiles.map((f, i) => (
            <div key={i} className={`rounded-xl border px-4 py-3 ${f.status === 'error' ? 'bg-destructive/5 border-destructive/20' : 'bg-muted/30'}`}>
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(f.size)}</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {f.status === 'analysing' && (
                    <><Sparkles className="h-3.5 w-3.5 animate-pulse" aria-hidden="true" /> Analysing…</>
                  )}
                  {f.status === 'done' && (
                    <><CheckCircle className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                      <span className="text-primary">{f.splitCount} {f.splitCount === 1 ? 'artefact' : 'artefacts'} found</span>
                    </>
                  )}
                  {f.status === 'error' && (
                    <><AlertCircle className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
                      <span className="text-destructive">
                        {f.errorReason === 'timeout' ? 'Timed out' : f.errorReason === 'read_error' ? 'Unreadable' : 'AI error'}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {f.status === 'error' && (
                <p className="text-xs text-muted-foreground mt-2 ml-7">
                  {f.errorReason === 'timeout'
                    ? 'The file is too large for automatic analysis. Upload it below and fill in the details manually.'
                    : f.errorReason === 'read_error'
                      ? 'The file could not be read. Try a .txt or .docx format.'
                      : 'The AI could not parse this file. You can still save it as a manual artefact.'}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Split detected ────────────────────────────────────────────────────────
  if (mode === 'split-detected') {
    const multiProject = new Set(splits.map((s) => s.projectId)).size > 1;
    return (
      <div className="max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Here's what I found</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {splits.length === 0
              ? 'Nothing detected — try the manual form.'
              : multiProject
                ? `Detected ${splits.length} segments across multiple projects — review each, then save.`
                : `Detected ${splits.length === 1 ? 'one segment' : `${splits.length} segments`} — confirm the details below, then save.`}
          </p>
        </div>

        <div className="space-y-4">
          {splits.map((split, i) => (
            <SplitCard
              key={i}
              index={i}
              split={split}
              projects={projects}
              onChange={updateSplit}
              onRemove={removeSplit}
            />
          ))}
        </div>

        {splits.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">All removed — use the manual form or drop a different file.</p>
        )}

        {mutationError && (
          <div className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-center gap-2 text-destructive text-sm" role="alert">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to save. Please try again.</span>
          </div>
        )}

        <div className="mt-6 flex gap-3 items-center">
          <Button
            type="button"
            variant="primary"
            disabled={isPending || splits.length === 0}
            onClick={handleSaveSplits}
          >
            {isPending
              ? `Saving ${savedCount}/${savingCount}…`
              : splits.length === 1
                ? 'Save artefact'
                : `Save ${splits.length} artefacts`}
          </Button>
          <button
            type="button"
            onClick={() => setMode('file-loaded')}
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Edit manually instead
          </button>
        </div>
      </div>
    );
  }

  // ── File-loaded / Manual form ─────────────────────────────────────────────
  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {mode === 'file-loaded' ? 'Confirm evidence' : 'Add evidence'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {mode === 'file-loaded'
            ? 'Review the detected fields and assign to a project before saving.'
            : 'Manually enter the evidence details.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>

        {/* File chip */}
        {mode === 'file-loaded' && (
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{fileName}</p>
              <p className="text-xs text-muted-foreground">{formatBytes(fileSize)}</p>
            </div>
            <button
              type="button"
              onClick={() => { setMode('dropzone'); setContent(''); setFileName(''); }}
              className="rounded-md p-1 hover:bg-accent"
              aria-label="Remove file"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium mb-1">
            Type <span className="text-destructive">*</span>
            {mode === 'file-loaded' && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">auto-detected</span>
            )}
          </label>
          <select
            id="type"
            value={type}
            onChange={(e) => setType(e.target.value as ArtefactTypeValue)}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
          >
            {ARTEFACT_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium mb-1">
            Title <span className="text-destructive">*</span>
            {mode === 'file-loaded' && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">from filename</span>
            )}
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(''); }}
            className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${titleError ? 'border-destructive' : ''}`}
            placeholder="Descriptive title for this evidence"
            maxLength={500}
          />
          {titleError && <p className="mt-1 text-xs text-destructive">{titleError}</p>}
        </div>

        {/* Project */}
        <div>
          <label htmlFor="projectId" className="block text-sm font-medium mb-1">Project</label>
          <select
            id="projectId"
            value={selectedProjectId}
            onChange={(e) => { setSelectedProjectId(e.target.value); setSelectedActivityId(''); }}
            disabled={projectsLoading || !!presetProjectId}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
          >
            <option value="">— no project —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.incomeYear ? ` (${formatFy(p.incomeYear)})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Activity tags */}
        {selectedProjectId && activities.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-1.5">Activity</label>
            <div className="flex flex-wrap gap-1.5">
              {activities.map((a) => {
                const isSelected = selectedActivityId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedActivityId(isSelected ? '' : a.id)}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'border bg-background text-muted-foreground hover:text-foreground hover:border-primary/50'
                    }`}
                  >
                    {isSelected && <Sparkles className="h-3 w-3" aria-hidden="true" />}
                    {a.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content — collapsible when file-loaded, always visible in manual */}
        <div>
          {mode === 'file-loaded' ? (
            <>
              <button
                type="button"
                onClick={() => setShowContent((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                aria-expanded={showContent}
              >
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${showContent ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
                {showContent ? 'Hide' : 'Preview'} content
                <span className="ml-1 text-xs font-normal">
                  ({content.split('\n').length} lines)
                </span>
              </button>
              {showContent && (
                <textarea
                  id="content"
                  rows={12}
                  value={content}
                  onChange={(e) => { setContent(e.target.value); if (contentError) setContentError(''); }}
                  className="mt-2 w-full rounded-lg border bg-background px-3 py-2 text-sm font-mono resize-y"
                />
              )}
            </>
          ) : (
            <>
              <label htmlFor="content" className="block text-sm font-medium mb-1">
                Content <span className="text-destructive">*</span>
              </label>
              <textarea
                id="content"
                rows={12}
                value={content}
                onChange={(e) => { setContent(e.target.value); if (contentError) setContentError(''); }}
                className={`w-full rounded-lg border bg-background px-3 py-2 text-sm font-mono resize-y ${contentError ? 'border-destructive' : ''}`}
                placeholder="Paste or write the evidence content here…"
              />
            </>
          )}
          {contentError && <p className="mt-1 text-xs text-destructive">{contentError}</p>}
        </div>

        {mutationError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-center gap-2 text-destructive text-sm" role="alert">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to create artefact. Please try again.</span>
          </div>
        )}

        {/* Summary line before submit */}
        {mode === 'file-loaded' && title && (
          <p className="text-xs text-muted-foreground">
            Saving as <span className="font-medium text-foreground">{typeLabel}</span>
            {selectedProjectId ? ` · ${projects.find((p) => p.id === selectedProjectId)?.name ?? ''}` : ''}
          </p>
        )}

        <div className="flex gap-3">
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? 'Saving…' : 'Save artefact'}
          </Button>
          <Link href={cancelHref}>
            <Button type="button" variant="ghost">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
